import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * Shared Terms & Conditions agreement text
 */
const TermsAndConditionsContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    className={cn("space-y-4 text-sm text-justify text-gray-700", className)}
  >
    <p>
      Welcome to QuickBill, an E-Invoice Intermediary Platform and cloud
      business software operated by PowerTree Solution Sdn. Bhd. ("QuickBill",
      "we", "us"). QuickBill connects your business systems to government
      e-invoicing platforms, including LHDN MyInvois (Malaysia) and IRAS
      InvoiceNow (Singapore). By registering for and using the platform you
      ("the User") agree to the following Terms &amp; Conditions. Please read
      them carefully.
    </p>

    <h3 className="font-semibold">1. Account Registration &amp; Eligibility</h3>
    <p>
      You must provide accurate, current, and complete information during
      registration and are responsible for keeping your account details up to
      date. Each account must be registered by a person authorised to act on
      behalf of the relevant business entity.
    </p>

    <h3 className="font-semibold">2. User Responsibilities</h3>
    <p>You are responsible for:</p>
    <ul className="list-disc pl-5 space-y-1">
      <li>Providing accurate and complete business information;</li>
      <li>
        Maintaining the security of your account and protecting your login
        credentials;
      </li>
      <li>Keeping all submitted information up to date;</li>
      <li>
        Ensuring the correctness of invoices and related data before submission;
        and
      </li>
      <li>Using the platform only for lawful purposes.</li>
    </ul>
    <p>
      Any misuse, unauthorised access, or fraudulent activity may result in
      suspension or termination of your account.
    </p>

    <h3 className="font-semibold">3. Regulatory Compliance</h3>
    <p>
      You remain solely responsible for ensuring your compliance with all
      applicable laws and regulations, including requirements imposed by LHDN
      MyInvois, IRAS InvoiceNow (where applicable), and other relevant
      regulatory authorities. QuickBill provides tools to facilitate compliance
      but does not replace professional accounting, legal, or tax advice.
    </p>

    <h3 className="font-semibold">4. Data Accuracy</h3>
    <p>
      QuickBill facilitates the submission and management of documents but
      cannot guarantee that information entered by users is accurate. You should
      carefully review and verify all information before submission. You are
      responsible for the accuracy and completeness of the data you submit.
    </p>

    <h3 className="font-semibold">5. Third-Party Integrations</h3>
    <p>
      The platform integrates with external systems and services such as LHDN,
      IRAS, ERP systems, and accounting software. QuickBill cannot guarantee the
      availability, performance, or behaviour of these third-party services, and
      is not responsible for issues arising from them.
    </p>

    <h3 className="font-semibold">6. AI-Assisted Features</h3>
    <p>
      Certain features of the platform use artificial intelligence to
      classify, extract, or generate data (for example, document data
      extraction and workflow automation). AI-generated output may contain
      errors or omissions and is provided to assist — not replace — your own
      review. You must verify all AI-assisted output before relying on it or
      submitting it to any regulatory authority.
    </p>

    <h3 className="font-semibold">7. Service Availability</h3>
    <p>
      The services are provided on a best-effort basis. Temporary downtime or
      interruptions may occur due to maintenance, government platform outages,
      network issues, or third-party infrastructure. We do not warrant
      uninterrupted or error-free operation of the platform.
    </p>

    <h3 className="font-semibold">8. Limitation of Liability</h3>
    <p>
      To the maximum extent permitted by law, QuickBill and PowerTree Solution
      Sdn. Bhd. shall not be liable for any loss or damage arising from,
      including but not limited to:
    </p>
    <ul className="list-disc pl-5 space-y-1">
      <li>
        Incorrect information or invoice data entered or submitted by users;
      </li>
      <li>Regulatory penalties arising from user errors;</li>
      <li>Business losses, loss of revenue, or loss of profits;</li>
      <li>Indirect, incidental, or consequential damages;</li>
      <li>Decisions made based on system-generated information; and</li>
      <li>
        Service interruptions caused by third-party providers or government
        systems.
      </li>
    </ul>
    <p>
      The platform is provided "as is" and "as available" without warranties of
      any kind.
    </p>

    <h3 className="font-semibold">9. Data Privacy</h3>
    <p>
      We collect and process your personal and business information in
      accordance with our{" "}
      <a
        className="text-blue-700 hover:underline"
        href="/privacy-policy"
        target="_blank"
        rel="noreferrer"
      >
        Privacy Policy
      </a>
      . By registering, you consent to our data handling practices.
    </p>

    <h3 className="font-semibold">10. Changes to Terms</h3>
    <p>
      These Terms &amp; Conditions may be updated from time to time. Your
      continued use of the platform following any changes constitutes acceptance
      of the revised terms.
    </p>
  </div>
));

TermsAndConditionsContent.displayName = "TermsAndConditionsContent";

export default TermsAndConditionsContent;
